import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs/index';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import {
    BsModalConfirmComponent,
    ConfirmDialogResult,
    createConfirmModalOptions
} from '../../core/modal/modal-confirm.component';
import { ReasonsSettingsActions } from '../../reasons/state/reasons-settings.actions';
import { AppointmentResourceSettingsActions } from './appointment-resource-settings.actions';
import { AppointmentResource } from './appointment-resource-settings.model';
import { AppointmentResourceSettingsService } from './appointment-resource-settings.service';

@Injectable()
export class AppointmentResourceSettingsEffects {
    constructor(
        private actions$: Actions,
        private appointmentResourceSettingsService: AppointmentResourceSettingsService,
        private modalService: BsModalService) {
    }

    getAppointmentResources$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AppointmentResourceSettingsActions.getAppointmentResources),
            switchMap((action: any) =>
                this.appointmentResourceSettingsService.getAppointmentResources(action.userid)
                    .pipe(
                        map((payload: Array<AppointmentResource>) =>
                                AppointmentResourceSettingsActions.getAppointmentResourcesSuccess({payload})
                        ),
                        catchError(error =>
                            of(AppointmentResourceSettingsActions.getAppointmentResourcesFail({ error }))
                        )
                    )
            )
        )
    );

    attemptSaveAppointmentResourceStrategyMapping$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AppointmentResourceSettingsActions.attemptSaveResourcesStrategyMapping),
            mergeMap(({resourceId, strategyId}) =>
                this.appointmentResourceSettingsService.getEstimatedAppointmentsAffected(resourceId, strategyId)
                    .pipe(
                        map((response: any) => {
                                const dialogMessage = `This mapping will affect ${response.estimatedAffected} appointments.
                                Are you sure you want to make this change?`;

                                return AppointmentResourceSettingsActions.openConfirmDialog({
                                    dialogMessage,
                                    resourceId, strategyId,
                                    okLabel: 'Yes', cancelLabel: 'No'
                                });

                                // return AppointmentResourceSettingsActions.getAppointmentResources();
                            }
                        )
                        // TODO: Handle when getting estimate fails
                    )
            )
        )
    );

    openConfirmDialog$ = createEffect(() =>
        this.actions$
            .pipe(
                ofType(AppointmentResourceSettingsActions.openConfirmDialog),
                switchMap(({dialogMessage, resourceId, strategyId, okLabel, cancelLabel}) => {
                    const message = dialogMessage;
                    const title = 'Confirm';
                    const data: any = { resourceId , strategyId };
                    const modalRef = this.modalService
                        .show(BsModalConfirmComponent,
                            createConfirmModalOptions(title, message, okLabel, cancelLabel, data));

                    return modalRef.content.onClose;
                }),
                map((dialogResult: ConfirmDialogResult) => { // map dialog result into action (map function returns observable to action)
                    if (dialogResult.isConfirmed) {
                        return AppointmentResourceSettingsActions.saveResourcesStrategyMapping({
                            resourceId: dialogResult.data.resourceId,
                            strategyId: dialogResult.data.strategyId
                        });
                    }

                    // Used cancelled saving
                    return AppointmentResourceSettingsActions.openConfirmDialogCancel();
                })
            )
    );

    saveAppointmentResourceStrategyMapping$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AppointmentResourceSettingsActions.saveResourcesStrategyMapping),
            mergeMap(({resourceId, strategyId}) =>

                this.appointmentResourceSettingsService.saveAppointmentResourceStrategyMapping(resourceId, strategyId)
                    .pipe(
                        map((response: any) => // Reload to get latest
                            AppointmentResourceSettingsActions.getAppointmentResources(undefined)
                        ),
                        catchError(error =>
                            of(AppointmentResourceSettingsActions
                                .saveAppointmentResourceStrategyMappingFail({ error, resourceId, strategyId }))
                        )
                    )
            )
        )
    );

    saveAppointmentResourceStrategyMappingFail$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AppointmentResourceSettingsActions.saveAppointmentResourceStrategyMappingFail),
            map(({resourceId, strategyId}) =>
                AppointmentResourceSettingsActions.openConfirmDialog({
                    dialogMessage: 'Saving Appointment Resource Strategy failed. Do you want to retry?',
                    resourceId, strategyId,
                    okLabel: 'Retry', cancelLabel: 'Cancel'
                }))
        )
    );


    getAppointmentResourcesAndResources$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AppointmentResourceSettingsActions.getAppointmentResourcesAndResources),
            switchMap((action: any) =>
                this.appointmentResourceSettingsService.getAppointmentResourcesAndResources(action.userid)
                    .pipe(
                        map((payload: Array<AppointmentResource>) =>
                            AppointmentResourceSettingsActions.getAppointmentResourcesSuccess({payload})
                        ),
                        catchError(error =>
                            of(AppointmentResourceSettingsActions.getAppointmentResourcesFail({ error }))
                        )
                    )
            )
        )
    );

}
